/**
 * scanners.ts — single source for "what scanners does a team have looking at
 * a given system this tick?"
 *
 * The detection pass (advance-detection.ts) and the fog-mask renderer both
 * need the same list — team-owned ships in the system plus team-owned planets
 * in the system, each packaged as a uniform ScannerView. Centralising the
 * collection here means v2 extensions ship in one place:
 *   - v2: deep-space sensor structures on planet P in system A contribute a
 *     synthetic ScannerRow to system B's list when their cross-system range
 *     covers B. Callers never change.
 *   - v2: a sibling helper `collectTeamHyperspaceScanners` will return the
 *     scanners that can see into the mid-jump pseudo-bucket.
 *
 * Keep the signature narrow — `(state, teamId, systemId, balance)` — so the
 * detection pass and the renderer can call it identically. The balance
 * reference lets us fold in VISION_RANGE_SCALE / SCAN_RANGE_SCALE and the
 * per-size planet tables without the caller doing arithmetic.
 */

import {
  DEFAULT_DETECTION_BALANCE,
  planetDetectionForSize,
  planetScanRangeForSize,
  planetStealthForSize,
  planetVisionRangeForSize,
  type DetectionBalance,
} from './balance-detection';
import { effectiveDetection, type ScannerView } from './detection';
import { sensorsEmpireBonus, sensorsScanRangeMultiplier } from './research-effects';
import { auraBonusesForShip } from './auras';
import type { GameState, SystemId, TeamId } from '../state/types/index';

// v4M10.7 — `lrsaScanBoostForPlanet` retired per `docs/SENSORS.md §2.3`.
// The v3M1 LRSA in-system additive scan boost was removed from
// `collectTeamScannersForSystem` at v4M10.6 (LRSA's new role is cross-
// system reveal via `applyLrsaRevealsForTick`); v4M10.7 drops the now-
// dead helper + its `lrsaScanBoostForLevel` dependency entirely.

/**
 * Alias retained for callers that want to name the collected rows distinctly
 * from the pure-math ScannerView type. Structurally identical — just a doc
 * handle for "this is a scanner row from the collector, not a hand-built
 * struct for a test".
 */
export type ScannerRow = ScannerView;

/**
 * Return every scanner contributing to `teamId`'s vision/scan footprint in
 * `systemId` this tick. Order is not semantically meaningful — the detection
 * pass takes `max(pV, pS)` across the list so permutations don't change the
 * result. planetStealthForSize is exposed via the balance module because
 * planets will also figure as *targets* for enemy scanners in a future
 * pass; kept co-located so all planet-sensor knobs live in one place.
 */
export function collectTeamScannersForSystem(
  state: GameState,
  teamId: TeamId,
  systemId: SystemId,
  balance: DetectionBalance = DEFAULT_DETECTION_BALANCE,
): ScannerRow[] {
  const rows: ScannerRow[] = [];

  // v4M3.2 — team-vision union. The `teamId` parameter is still the
  // viewer's empire id (legacy naming preserved across the codebase);
  // when that empire belongs to a non-null `Empire.teamId`, the
  // scanner pool unions ships + planets across every teammate
  // empire. Solo viewers (`teamId === null`) get the v1 single-empire
  // scope. Authoritative spec: `docs/TEAMS.md §3`.
  const viewerEmpire = state.empires.get(teamId);
  const teamMembers = new Set<TeamId>([teamId]);
  if (viewerEmpire && viewerEmpire.teamId !== null) {
    for (const [id, e] of state.empires) {
      if (e.teamId === viewerEmpire.teamId) teamMembers.add(id);
    }
  }
  const ownsScanner = (ownerId: number): boolean => teamMembers.has(ownerId);

  // 1) Team-owned ships currently in the system (skip mid-jump — they don't
  //    see anything while in hyperspace; a v2 deep-space scanner structure
  //    will cover that case via a separate helper).
  for (const ship of state.ships.values()) {
    if (ship.systemId !== systemId) continue;
    if (!ownsScanner(ship.ownerEmpireId)) continue;
    const empire = state.empires.get(ship.ownerEmpireId);
    if (!empire) continue;
    const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
    if (!tmpl) continue;
    // v3M2.2 — ring platforms carry a chassisLevel that scales vision
    // and scan ranges beyond the template baseline. Same +20%/level
    // curve as hull, capped at 2.0 at L6.
    let chassisMult = 1.0;
    if (ship.chassisLevel !== undefined && ship.chassisLevel > 1) {
      chassisMult = Math.min(2.0, 1.0 + (ship.chassisLevel - 1) * 0.2);
    }
    rows.push({
      sx: ship.sx,
      sy: ship.sy,
      visionRange: tmpl.visionRange * chassisMult,
      // MFix8.3 — Sensors Mk research multiplies effective scan range
      // empire-wide (on top of the chassis-level scaling for ODP rings).
      scanRange:
        tmpl.scanRange * chassisMult * sensorsScanRangeMultiplier(state, empire),
      // MFix7.2 — Sensors Mk research adds a flat empire-wide bonus
      // to detectionBase (Non-combat: Sensors category in tech tree).
      // MFix8.5 — friendly aura `detectionBonus` (e.g. Magician) is
      // additive on top of the empire bonus. Aura helper short-circuits
      // to zero when no eligible emitters cover this ship.
      detectionEffective: effectiveDetection(
        tmpl.detectionBase +
          sensorsEmpireBonus(state, empire) +
          auraBonusesForShip(state, ship).detectionBonus,
        balance,
      ),
    });
  }

  // 2) Team-owned planets in the system as orbital sensors (PLANETS.md).
  //    MFix1.5 — flat-constant vision/scan/detection from balance-detection
  //    (Cruiser-comparable). Mid-jump ships are skipped above; planets are
  //    always in-system so no equivalent guard is needed here.
  //    v4M10.6 — legacy `lrsaScanBoostForPlanet` in-system additive boost
  //    retired per `docs/SENSORS.md §2.3`. LRSA's new role is cross-
  //    system reveal via `applyLrsaRevealsForTick` in `src/state/fow.ts`;
  //    in-system scanning continues via per-ship + per-planet base scan
  //    range. Tune `balanceData.detection.planetScanRange` if playtest
  //    finds the base reach insufficient, NOT by reverting this boost.
  //    v4M10.7 — dead `lrsaScanBoostFor*` helpers + the
  //    `features.lrsa.scanBoostByLevel` data field fully retired.
  //    v4M10.7 follow-up part 2 — note for archaeologists: Sensor Buoy
  //    + other Space Structures (Repair Yard, Warp Gate, Minefield,
  //    extractors, weapon platforms) are NOT planet structures. They
  //    spawn as stationary ships per `STRUCTURES.md §3`, live in
  //    `state.ships`, and their perception stats flow through the
  //    template.scanRange path (the loop at #1 above) — NOT through
  //    this planet-scanner loop. The dormant `scanRange`
  //    `StructureEffect` variant was retired in the same v4M10.7
  //    follow-up since LRSA was its only consumer.
  for (const planet of state.planets.values()) {
    if (planet.systemId !== systemId) continue;
    // v4M3.2 — same team-union pattern as ship scanners above.
    if (planet.ownerEmpireId === null || !ownsScanner(planet.ownerEmpireId)) continue;
    const planetEmpire = state.empires.get(planet.ownerEmpireId);
    // MFix7.2 — Sensors Mk research adds the same flat empire-wide
    // bonus to planet detection as it does to ship detection.
    const sensorsBonus = planetEmpire ? sensorsEmpireBonus(state, planetEmpire) : 0;
    // MFix8.3 — Sensors Mk research multiplies the planet's scan range.
    // Defaults to 1.0 when no Sensors tech is researched, so vanilla
    // planet sensors are unchanged until the empire moves up the ladder.
    const sensorsScanMult = planetEmpire
      ? sensorsScanRangeMultiplier(state, planetEmpire)
      : 1.0;
    rows.push({
      sx: planet.systemX,
      sy: planet.systemY,
      visionRange: planetVisionRangeForSize(planet.size, state.balanceData),
      scanRange:
        planetScanRangeForSize(planet.size, state.balanceData) *
        sensorsScanMult,
      detectionEffective: effectiveDetection(
        planetDetectionForSize(planet.size, state.balanceData) + sensorsBonus,
        balance,
      ),
    });
  }

  return rows;
}

// Re-export the planet stealth helper so callers (e.g. a future "planets as
// targets" pass) can reach it without hopping back to balance-detection.
export { planetStealthForSize };
